import React, { useEffect, useMemo, useState } from 'react';
import {
    CAlert,
    CButton,
    CButtonGroup,
    CCard,
    CCardBody,
    CCol,
    CFormInput,
    CFormLabel,
    CFormSelect,
    CFormSwitch,
    CFormTextarea,
    CRow,
    CSpinner,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilArrowLeft, cilCloudUpload } from '@coreui/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as DomicilappService from '../../services/DomicilappService';
import { useStore } from '../../contexts/StoreContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
    AdPayload,
    AdsPricing,
    AudienceSegment,
    BILLING_CLICK,
    BILLING_IMPRESSION,
    BILLING_PER_USER,
    DiscountItem,
    DiscountType,
    SERVICE_BANNER,
    SERVICE_FLASH,
    SERVICE_PUSH,
    SERVICE_TYPE_COUPONS,
    SERVICE_TYPE_EVENTS,
    SERVICE_TYPE_OFFERS,
    SERVICE_TYPE_PROMOTIONS,
} from '../../types/marketing';
import { discountLabel, formatCOP, inputDateFromNow, toInputDate } from './marketingUtils';

// Qué se anuncia (service_types) y de dónde sale el contenido
const CONTENT_OPTIONS: { serviceType: number; label: string; plural: string; discountType: DiscountType }[] = [
    { serviceType: SERVICE_TYPE_OFFERS, label: 'Oferta', plural: 'ofertas', discountType: 'offers' },
    { serviceType: SERVICE_TYPE_PROMOTIONS, label: 'Promoción', plural: 'promociones', discountType: 'promotions' },
    { serviceType: SERVICE_TYPE_COUPONS, label: 'Cupón', plural: 'cupones', discountType: 'coupons' },
    { serviceType: SERVICE_TYPE_EVENTS, label: 'Evento', plural: 'eventos', discountType: 'events' },
];

const AUDIENCES: { value: AudienceSegment; label: string }[] = [
    { value: 'municipality', label: 'Clientes de tu municipio' },
    { value: 'followers', label: 'Tus seguidores (te marcaron como favorita)' },
    { value: 'customers', label: 'Clientes que ya te compraron' },
];

const toBackendDate = (value: string) => (value ? value.replace('T', ' ') + ':00' : '');

const AdForm: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { storeId } = useStore();
    const { showToast } = useNotification();

    const [pricing, setPricing] = useState<AdsPricing | null>(null);
    const [serviceType, setServiceType] = useState<number>(Number(searchParams.get('type')) || SERVICE_TYPE_OFFERS);
    const [contents, setContents] = useState<DiscountItem[]>([]);
    const [contentId, setContentId] = useState<number | null>(Number(searchParams.get('content')) || null);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [buttonText, setButtonText] = useState('Ver tienda');
    const [conditionsText, setConditionsText] = useState('');
    const [startDate, setStartDate] = useState(inputDateFromNow(0));
    const [endDate, setEndDate] = useState(inputDateFromNow(7));
    const [image, setImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    const [displayService, setDisplayService] = useState<number | null>(SERVICE_BANNER);
    const [budget, setBudget] = useState<number>(20000);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [audience, setAudience] = useState<AudienceSegment>('municipality');
    const [audienceCount, setAudienceCount] = useState<number | null>(null);
    const [scheduleDate, setScheduleDate] = useState('');
    const [saving, setSaving] = useState(false);

    const contentOption = CONTENT_OPTIONS.find((c) => c.serviceType === serviceType)!;
    const selectedContent = contents.find((c) => c.id === contentId);
    // Un evento con imagen no necesita otra para el anuncio
    const fallbackImage = serviceType === SERVICE_TYPE_EVENTS ? selectedContent?.image_url ?? null : null;

    useEffect(() => {
        if (!storeId) return;
        DomicilappService.getAdsPricing(storeId).then((response) => setPricing(response?.status === 200 ? response.data : null));
    }, [storeId]);

    // Ofertas / promociones / cupones vigentes para elegir
    useEffect(() => {
        setContents([]);
        if (!storeId) return;
        DomicilappService.getDiscounts(storeId, contentOption.discountType).then((response) => {
            const items = (response?.data?.items ?? []).filter((item) => item.status === 'active' || item.status === 'scheduled');
            setContents(items);
            setContentId((prev) => (items.some((item) => item.id === prev) ? prev : items[0]?.id ?? null));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, serviceType]);

    // Textos sugeridos a partir del descuento elegido
    useEffect(() => {
        if (!selectedContent) return;
        const isEvent = serviceType === SERVICE_TYPE_EVENTS;
        setTitle(
            (isEvent ? selectedContent.name : `${discountLabel(selectedContent.discount_types_id, selectedContent.discount_value)} OFF · ${selectedContent.name}`).slice(0, 80)
        );
        setDescription((selectedContent.description ?? '').slice(0, 255));
        if (isEvent) setButtonText('Ver evento');
        setConditionsText(selectedContent.code ? `Usa el código ${selectedContent.code}` : '');
        setEndDate(toInputDate(selectedContent.end_date));
    }, [selectedContent]);

    useEffect(() => {
        if (!storeId || !pushEnabled) return;
        setAudienceCount(null);
        DomicilappService.getAdsAudience(storeId, audience).then((response) => setAudienceCount(response?.status === 200 ? response.data.count : 0));
    }, [storeId, pushEnabled, audience]);

    const price = (serviceId: number, billingTypeId: number) =>
        pricing?.prices.find((p) => p.service_id === serviceId && p.service_type_id === serviceType && p.billing_type_id === billingTypeId)?.price ?? null;

    const impressionPrice = displayService ? price(displayService, BILLING_IMPRESSION) : null;
    const clickPrice = displayService ? price(displayService, BILLING_CLICK) : null;
    const pushPrice = price(SERVICE_PUSH, BILLING_PER_USER);
    const minBudget = impressionPrice !== null && clickPrice !== null ? (impressionPrice + clickPrice) * 10 : 0;

    const displayCost = displayService ? budget : 0;
    const pushCost = pushEnabled && pushPrice !== null && audienceCount !== null ? audienceCount * pushPrice : 0;
    const total = displayCost + pushCost;
    const balance = pricing?.balance ?? 0;

    const problems = useMemo(() => {
        const list: string[] = [];
        if (!contentId) list.push(`No tienes ${contentOption.plural} vigentes. Créalos en Descuentos y eventos.`);
        if (!title.trim()) list.push('Escribe el título del anuncio.');
        if (!displayService && !pushEnabled) list.push('Elige al menos un canal.');
        if (displayService && (impressionPrice === null || clickPrice === null)) list.push('Tu plan no incluye este canal.');
        if (displayService && budget < minBudget) list.push(`El presupuesto mínimo es ${formatCOP(minBudget)}.`);
        if (displayService && !image && !fallbackImage) list.push('El banner y el flash message necesitan una imagen.');
        if (pushEnabled && pushPrice === null) list.push('Tu plan no incluye notificaciones push.');
        if (pushEnabled && audienceCount === 0) list.push('No hay clientes con la app en esa audiencia.');
        if (total > balance) list.push(`Saldo insuficiente: te faltan ${formatCOP(total - balance)}.`);
        return list;
    }, [contentOption, contentId, title, displayService, pushEnabled, impressionPrice, clickPrice, budget, minBudget, image, fallbackImage, pushPrice, audienceCount, total, balance]);

    const handleImage = (file: File | null) => {
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImage(file);
        setImagePreview(file ? URL.createObjectURL(file) : null);
    };

    const handlePublish = () => {
        const payload: AdPayload = {
            service_types_id: serviceType,
            content_id: contentId,
            title: title.trim(),
            description,
            button_text: buttonText,
            conditions_text: conditionsText,
            start_date: toBackendDate(startDate),
            end_date: toBackendDate(endDate),
            display: { service_id: displayService, budget },
            push: { enabled: pushEnabled, audience, schedule_date: scheduleDate ? toBackendDate(scheduleDate) : null },
        };
        const fd = new FormData();
        fd.append('data', JSON.stringify(payload));
        if (image) fd.append('image', image, image.name);

        setSaving(true);
        DomicilappService.createStoreAd(storeId, fd)
            .then((response: any) => {
                if (response?.status === 200) {
                    showToast({ title: '¡Campaña publicada!', message: `Se descontaron ${formatCOP(response.data.total)} de tu saldo`, type: 'success', delay: 4000 });
                    navigate('/marketing/ads');
                } else {
                    showToast({ title: 'No se pudo publicar', message: typeof response?.data?.error === 'string' ? response.data.error : response?.message, type: 'warning', delay: 5000 });
                }
            })
            .catch(() => showToast({ title: '¡Ha ocurrido un error!', message: 'No fue posible publicar la campaña', type: 'danger', delay: 3500 }))
            .finally(() => setSaving(false));
    };

    return (
        <div className="ad-form-page pb-5">
            <div className="d-flex align-items-center gap-3 mb-4">
                <CButton color="light" onClick={() => navigate('/marketing/ads')}>
                    <CIcon icon={cilArrowLeft} />
                </CButton>
                <div>
                    <h3 className="fw-bold text-body mb-0">Nueva campaña</h3>
                    <p className="text-body-secondary small mb-0">Solo pagas la difusión; el costo depende de tu plan ({pricing?.plan_name ?? '...'})</p>
                </div>
            </div>

            <CRow className="g-4">
                <CCol lg={8}>
                    {/* ---- QUÉ ANUNCIAS ---- */}
                    <CCard className="border-0 shadow-sm rounded-3 mb-4">
                        <CCardBody className="p-4">
                            <h6 className="fw-bold text-body mb-3">¿Qué quieres anunciar?</h6>
                            <CButtonGroup className="mb-3 flex-wrap">
                                {CONTENT_OPTIONS.map((option) => (
                                    <CButton
                                        key={option.serviceType}
                                        color={serviceType === option.serviceType ? 'dark' : 'light'}
                                        onClick={() => setServiceType(option.serviceType)}
                                    >
                                        {option.label}
                                    </CButton>
                                ))}
                            </CButtonGroup>
                            <CFormSelect value={contentId ?? ''} onChange={(e) => setContentId(Number(e.target.value) || null)}>
                                {contents.length === 0 && <option value="">No tienes {contentOption.plural} vigentes</option>}
                                {contents.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {serviceType === SERVICE_TYPE_EVENTS
                                            ? `${item.name} — ${item.start_date.slice(0, 16)}`
                                            : `${item.name} — ${discountLabel(item.discount_types_id, item.discount_value)}${item.code ? ` (${item.code})` : ''}`}
                                    </option>
                                ))}
                            </CFormSelect>
                            {serviceType === SERVICE_TYPE_EVENTS && (
                                <small className="text-body-secondary d-block mt-2">El anuncio abre el detalle del evento en la sección Eventos de la app.</small>
                            )}
                        </CCardBody>
                    </CCard>

                    {/* ---- ANUNCIO ---- */}
                    <CCard className="border-0 shadow-sm rounded-3 mb-4">
                        <CCardBody className="p-4">
                            <h6 className="fw-bold text-body mb-3">El anuncio</h6>
                            <CRow className="g-3">
                                <CCol md={8}>
                                    <CFormLabel className="small fw-semibold">Título</CFormLabel>
                                    <CFormInput value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
                                </CCol>
                                <CCol md={4}>
                                    <CFormLabel className="small fw-semibold">Texto del botón</CFormLabel>
                                    <CFormInput value={buttonText} maxLength={40} onChange={(e) => setButtonText(e.target.value)} />
                                </CCol>
                                <CCol md={12}>
                                    <CFormLabel className="small fw-semibold">Descripción</CFormLabel>
                                    <CFormTextarea rows={2} maxLength={255} value={description} onChange={(e) => setDescription(e.target.value)} />
                                </CCol>
                                <CCol md={12}>
                                    <CFormLabel className="small fw-semibold">Condiciones (texto pequeño, opcional)</CFormLabel>
                                    <CFormInput value={conditionsText} maxLength={120} placeholder="Ej. Hasta agotar existencias" onChange={(e) => setConditionsText(e.target.value)} />
                                </CCol>
                                <CCol md={6}>
                                    <CFormLabel className="small fw-semibold">Se muestra desde</CFormLabel>
                                    <CFormInput type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                </CCol>
                                <CCol md={6}>
                                    <CFormLabel className="small fw-semibold">Hasta</CFormLabel>
                                    <CFormInput type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                </CCol>
                                <CCol md={12}>
                                    <label
                                        htmlFor="ad-image-input"
                                        className="d-flex align-items-center justify-content-center gap-3 border rounded-3 bg-body-tertiary overflow-hidden"
                                        style={{ borderStyle: 'dashed', minHeight: '140px', cursor: 'pointer' }}
                                    >
                                        {imagePreview || fallbackImage ? (
                                            <img src={imagePreview ?? fallbackImage ?? ''} alt="" style={{ maxHeight: '180px', maxWidth: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <span className="text-body-secondary small d-flex align-items-center gap-2">
                                                <CIcon icon={cilCloudUpload} size="lg" /> Imagen del anuncio (horizontal, recomendado 1200×600)
                                            </span>
                                        )}
                                        <input id="ad-image-input" type="file" accept="image/*" className="d-none" onChange={(e) => handleImage(e.target.files?.[0] ?? null)} />
                                    </label>
                                </CCol>
                            </CRow>
                        </CCardBody>
                    </CCard>

                    {/* ---- CANALES ---- */}
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-4">
                            <h6 className="fw-bold text-body mb-1">Dónde se ve</h6>
                            <p className="text-body-secondary small">Banner y flash message se pagan por impresión y por click con un presupuesto que defines; lo que no se use vuelve a tu saldo.</p>
                            <CButtonGroup className="mb-3">
                                {[
                                    { value: SERVICE_BANNER, label: 'Banner' },
                                    { value: SERVICE_FLASH, label: 'Flash message' },
                                    { value: null, label: 'Ninguno' },
                                ].map((option) => (
                                    <CButton key={String(option.value)} color={displayService === option.value ? 'dark' : 'light'} onClick={() => setDisplayService(option.value)}>
                                        {option.label}
                                    </CButton>
                                ))}
                            </CButtonGroup>
                            {displayService && (
                                <CRow className="g-3 align-items-end mb-4">
                                    <CCol md={5}>
                                        <CFormLabel className="small fw-semibold">Presupuesto</CFormLabel>
                                        <CFormInput type="number" min={minBudget} step={1000} value={budget} onChange={(e) => setBudget(Number(e.target.value) || 0)} />
                                    </CCol>
                                    <CCol md={7} className="small text-body-secondary">
                                        {impressionPrice !== null && clickPrice !== null ? (
                                            <>
                                                {formatCOP(impressionPrice)} por impresión · {formatCOP(clickPrice)} por click
                                                <br />≈ {Math.floor(budget / impressionPrice).toLocaleString('es-CO')} personas lo ven (sin clicks)
                                            </>
                                        ) : (
                                            <span className="text-warning">Tu plan no incluye este canal.</span>
                                        )}
                                    </CCol>
                                </CRow>
                            )}

                            <div className="border-top pt-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="fw-bold text-body mb-0">Notificación push</h6>
                                        <small className="text-body-secondary">
                                            {pushPrice !== null ? `${formatCOP(pushPrice)} por cliente al que se envía` : 'Tu plan no incluye notificaciones push'}
                                        </small>
                                    </div>
                                    <CFormSwitch checked={pushEnabled} disabled={pushPrice === null} onChange={(e) => setPushEnabled(e.target.checked)} />
                                </div>
                                {pushEnabled && (
                                    <CRow className="g-3 mt-1">
                                        <CCol md={7}>
                                            <CFormLabel className="small fw-semibold">A quién</CFormLabel>
                                            <CFormSelect value={audience} onChange={(e) => setAudience(e.target.value as AudienceSegment)}>
                                                {AUDIENCES.map((a) => (
                                                    <option key={a.value} value={a.value}>{a.label}</option>
                                                ))}
                                            </CFormSelect>
                                            <small className="text-body-secondary">
                                                {audienceCount === null ? 'Calculando...' : `${audienceCount.toLocaleString('es-CO')} clientes con la app`}
                                            </small>
                                        </CCol>
                                        <CCol md={5}>
                                            <CFormLabel className="small fw-semibold">Enviar (opcional)</CFormLabel>
                                            <CFormInput type="datetime-local" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} />
                                            <small className="text-body-secondary">Vacío = al iniciar el anuncio</small>
                                        </CCol>
                                    </CRow>
                                )}
                            </div>
                        </CCardBody>
                    </CCard>
                </CCol>

                {/* ---- COSTO ---- */}
                <CCol lg={4}>
                    <CCard className="border-0 shadow-sm rounded-3 position-sticky" style={{ top: '80px' }}>
                        <CCardBody className="p-4">
                            <h6 className="fw-bold text-body mb-3">Costo</h6>
                            <div className="small">
                                {displayService && (
                                    <div className="d-flex justify-content-between mb-1">
                                        <span className="text-body-secondary">Presupuesto {displayService === SERVICE_BANNER ? 'banner' : 'flash message'}</span>
                                        <span className="text-body">{formatCOP(displayCost)}</span>
                                    </div>
                                )}
                                {pushEnabled && (
                                    <div className="d-flex justify-content-between mb-1">
                                        <span className="text-body-secondary">Push a {audienceCount ?? '...'} clientes</span>
                                        <span className="text-body">{formatCOP(pushCost)}</span>
                                    </div>
                                )}
                                <div className="d-flex justify-content-between border-top pt-2 mt-2 fw-bold">
                                    <span className="text-body">Total</span>
                                    <span className="text-body">{formatCOP(total)}</span>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                    <span className="text-body-secondary">Tu saldo</span>
                                    <span className={total > balance ? 'text-danger fw-semibold' : 'text-body'}>{formatCOP(balance)}</span>
                                </div>
                            </div>

                            {problems.length > 0 && (
                                <CAlert color="warning" className="small mt-3 mb-0 py-2">
                                    {problems.map((problem) => (
                                        <div key={problem}>• {problem}</div>
                                    ))}
                                </CAlert>
                            )}

                            <CButton color="dark" className="w-100 mt-3" disabled={saving || problems.length > 0} onClick={handlePublish}>
                                {saving && <CSpinner size="sm" className="me-1" />} Publicar y pagar {formatCOP(total)}
                            </CButton>
                            <small className="text-body-secondary d-block mt-2">
                                Se descuenta de tu saldo al publicar. Si terminas antes o no se consume todo el presupuesto, la diferencia vuelve a tu saldo.
                            </small>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>
        </div>
    );
};

export default AdForm;
